<template>
  <div class="overlapping-visits-queue-panel">
    <!--<NoteWithIcon v-if="props.hasPostedVisitAwaitingCreator" class="overlapping-visits-queue-panel__notice" color="yellow" size="small">
      <template #icon><IconEmbedded name="info-simple_4" :size="22" /></template>
      <template #text>
        <div class="overlapping-visits-queue-panel__notice-title">Another Visit is Currently out to Creators</div>
        <p class="global-p overlapping-visits-queue-panel__notice-subtext">
          We limit the number visits available at once to improve the number and variety of creators who apply for your visits. This visit will be sent to creators when the creator is chosen for the
          first visit.
        </p>
      </template>
    </NoteWithIcon>

    <NoteWithIcon v-if="props.hasReadyVisitNotYetPosted" class="overlapping-visits-queue-panel__notice" color="blue" size="small">
      <template #icon><IconEmbedded name="info-simple_4" :size="22" /></template>
      <template #text>
        <div class="overlapping-visits-queue-panel__notice-title">Another Visit will be sent to creators shortly.</div>
        <p class="global-p overlapping-visits-queue-panel__notice-subtext">
          This visit will be sent to creators when the creator is chosen for the first visit. You can change the order below. We recommend the earliest visit be submitted first.
        </p>
      </template>
    </NoteWithIcon>-->

    <h4 class="global-h4 overlapping-visits-queue-panel__list-title">What order should visits be sent to creators?</h4>
    <p class="global-p">Once a creator is chosen for the first visit, the next visit will be sent to creators.</p>

    <ul class="overlapping-visits-queue-panel__list">
      <li
        v-for="(row, index) in localQueue"
        :key="row.id"
        :class="{
          'overlapping-visits-queue-panel__row': true,
          'overlapping-visits-queue-panel__row--current': row.id === props.currentCollabInputId,
        }"
      >
        <div class="overlapping-visits-queue-panel__row-main">
          <div class="overlapping-visits-queue-panel__row-heading">
            <span class="overlapping-visits-queue-panel__visit-name">{{ row.name || "Untitled visit" }}</span>
            <span v-if="row.id === props.currentCollabInputId" class="overlapping-visits-queue-panel__badge overlapping-visits-queue-panel__badge--current">This visit</span>
            <span v-else-if="row.postedToCreators" class="overlapping-visits-queue-panel__badge overlapping-visits-queue-panel__badge--posted">Sent to creators</span>
            <!--<span v-else-if="row.readyToPost" class="overlapping-visits-queue-panel__badge overlapping-visits-queue-panel__badge--queued">Queued</span>-->
          </div>

          <p v-if="!row.postedToCreators" class="global-p overlapping-visits-queue-panel__send-note">- {{ willSendOutMessage(index) }}</p>

          <div class="overlapping-visits-queue-panel__date-row">
            <span class="global-p overlapping-visits-queue-panel__date-summary">
              Choose a creator by <b>{{ formatChooseCreatorDisplayDate(row.chooseCreatorEstimatedDate) }}</b>
            </span>
            <LinkWithIcon class="overlapping-visits-queue-panel__date-edit" isDottedUnderline color="blue" @click="openDatePicker(row.id)">
              <span>adjust</span>
            </LinkWithIcon>
            <input
              :ref="el => setDateInputRef(row.id, el as HTMLInputElement | null)"
              class="overlapping-visits-queue-panel__date-input-hidden"
              type="date"
              v-model="row.chooseCreatorEstimatedDate"
              :min="minDateForRow(index)"
              tabindex="-1"
              aria-hidden="true"
              @change="onDatePickerChanged(index)"
            />
          </div>

          <template v-if="isLateDateWarningVisible(row, index)">
            <NoteWithIcon class="overlapping-visits-queue-panel__late-warning" size="small">
              <template #text>Choosing a creator earlier helps lock in their availability and keeps your creator visit on track.</template>
            </NoteWithIcon>
            <textarea
              class="global-textarea overlapping-visits-queue-panel__estim-note"
              placeholder="Tell us why you need additional time. (optional)"
              v-model="row.chooseCreatorEstimatedNotes"
              @input="onRowNotesInput"
            />
          </template>
        </div>

        <div v-if="!row.postedToCreators" class="overlapping-visits-queue-panel__sort-controls">
          <button type="button" class="overlapping-visits-queue-panel__sort-btn" :disabled="!canMoveUp(index)" @click="moveUp(index)" aria-label="Move visit up in queue">
            <IconEmbedded name="caret-top_4" :size="12" />
          </button>
          <button type="button" class="overlapping-visits-queue-panel__sort-btn" :disabled="!canMoveDown(index)" @click="moveDown(index)" aria-label="Move visit down in queue">
            <IconEmbedded name="caret-bottom_4" :size="12" />
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import DayjsExtended from "@logic/DayjsExtended";

import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import { ChooseCreatorDaysLimitWithoutWarning, DaysBetweenOverlappingVisitChooseCreatorDates } from "@constants/collab";
import { formatChooseCreatorDateForInput, OverlappingVisitQueueRow, renormalizePostToCreatorsOrder, swapQueueRowsKeepingSlotDates } from "./overlappingVisitsQueueLogic";

const props = withDefaults(
  defineProps<{
    queue: OverlappingVisitQueueRow[];
    currentCollabInputId: string;
    hasPostedVisitAwaitingCreator: boolean;
    hasReadyVisitNotYetPosted: boolean;
  }>(),
  {
    queue: () => [],
    hasPostedVisitAwaitingCreator: false,
    hasReadyVisitNotYetPosted: false,
  }
);

const emit = defineEmits<{
  (e: "update:queue", value: OverlappingVisitQueueRow[]): void;
}>();

const localQueue = ref<OverlappingVisitQueueRow[]>([]);
const dateInputByRowId = ref<Record<string, HTMLInputElement | null>>({});

watch(
  () => props.queue,
  newQueue => {
    localQueue.value = newQueue.map(row => ({
      ...row,
      chooseCreatorEstimatedDate: formatChooseCreatorDateForInput(row.chooseCreatorEstimatedDate),
    }));
    const validIds = new Set(newQueue.map(row => row.id));
    const nextRefs: Record<string, HTMLInputElement | null> = {};
    for (const id of Object.keys(dateInputByRowId.value)) {
      if (validIds.has(id)) {
        nextRefs[id] = dateInputByRowId.value[id];
      }
    }
    dateInputByRowId.value = nextRefs;
  },
  { immediate: true, deep: true }
);

function setDateInputRef(rowId: string, el: HTMLInputElement | null) {
  if (el) {
    dateInputByRowId.value[rowId] = el;
  } else {
    delete dateInputByRowId.value[rowId];
  }
}

function openDatePicker(rowId: string) {
  const input = dateInputByRowId.value[rowId];
  if (!input) return;
  if (typeof input.showPicker === "function") {
    input.showPicker();
  } else {
    input.click();
  }
}

/** Same threshold as ChooseCreatorDateForm, but anchored to ready-to-post (row 0) or the choose-by date on the row above. */
function lateDateLimitForRow(index: number, row: OverlappingVisitQueueRow): ReturnType<typeof DayjsExtended> {
  if (index === 0) {
    const sendBaseline = row.readyToPost ?? row.postedToCreators;
    return (sendBaseline ? DayjsExtended(sendBaseline) : DayjsExtended()).add(ChooseCreatorDaysLimitWithoutWarning, "days");
  }
  const aboveChooseDate = localQueue.value[index - 1]?.chooseCreatorEstimatedDate;
  if (!aboveChooseDate) {
    return DayjsExtended().add(ChooseCreatorDaysLimitWithoutWarning, "days");
  }
  return DayjsExtended(aboveChooseDate).add(ChooseCreatorDaysLimitWithoutWarning, "days");
}

function isLateDateWarningVisible(row: OverlappingVisitQueueRow, index: number): boolean {
  if (!row.chooseCreatorEstimatedDate) return false;
  const selectedDate = DayjsExtended(row.chooseCreatorEstimatedDate);
  return selectedDate.isAfter(lateDateLimitForRow(index, row), "day");
}

function onRowNotesInput() {
  emitQueue([...localQueue.value]);
}

function formatChooseCreatorDisplayDate(date: string | null | undefined): string {
  if (!date) return "—";
  return DayjsExtended(date).format("MMMM D, YYYY");
}

function willSendOutMessage(index: number): string {
  if (index === 0) {
    /* const readyToPostDate = localQueue.value[0]?.readyToPost;
    if (readyToPostDate) {
      return `Will be sent to creators shortly (~${formatChooseCreatorDisplayDate(readyToPostDate)})`;
    }*/
    return "Will be sent to creators shortly.";
  }
  const aboveChooseDate = localQueue.value[index - 1]?.chooseCreatorEstimatedDate;
  if (!aboveChooseDate) {
    return "Will be sent to creators after you choose a creator above";
  }
  return `Will be sent to creators after you choose a creator above (~${formatChooseCreatorDisplayDate(aboveChooseDate)})`;
}

function onDatePickerChanged(changedIndex: number) {
  const row = localQueue.value[changedIndex];
  if (row && !isLateDateWarningVisible(row, changedIndex)) {
    const next = [...localQueue.value];
    next[changedIndex] = { ...next[changedIndex], chooseCreatorEstimatedNotes: null };
    localQueue.value = next;
  }
  onDateChanged(changedIndex);
}

function emitQueue(next: OverlappingVisitQueueRow[]) {
  const normalized = renormalizePostToCreatorsOrder(next);
  localQueue.value = normalized;
  emit("update:queue", normalized);
}

function nonPostedIndices(): number[] {
  return localQueue.value.map((row, index) => (!row.postedToCreators ? index : -1)).filter(index => index >= 0);
}

function canMoveUp(index: number): boolean {
  const reorderable = nonPostedIndices();
  const position = reorderable.indexOf(index);
  return position > 0;
}

function canMoveDown(index: number): boolean {
  const reorderable = nonPostedIndices();
  const position = reorderable.indexOf(index);
  return position >= 0 && position < reorderable.length - 1;
}

function moveUp(index: number) {
  const reorderable = nonPostedIndices();
  const position = reorderable.indexOf(index);
  if (position <= 0) return;
  const swapWith = reorderable[position - 1];
  emitQueue(swapQueueRowsKeepingSlotDates(localQueue.value, index, swapWith));
}

function moveDown(index: number) {
  const reorderable = nonPostedIndices();
  const position = reorderable.indexOf(index);
  if (position < 0 || position >= reorderable.length - 1) return;
  const swapWith = reorderable[position + 1];
  emitQueue(swapQueueRowsKeepingSlotDates(localQueue.value, index, swapWith));
}

function onDateChanged(changedIndex: number) {
  const next = [...localQueue.value];
  for (let i = changedIndex + 1; i < next.length; i++) {
    const previousDate = next[i - 1].chooseCreatorEstimatedDate;
    if (!previousDate) continue;
    next[i] = {
      ...next[i],
      chooseCreatorEstimatedDate: DayjsExtended(previousDate).add(DaysBetweenOverlappingVisitChooseCreatorDates, "days").format("YYYY-MM-DD"),
    };
  }
  emitQueue(next);
}

const todayMin = computed(() => DayjsExtended().format("YYYY-MM-DD"));

function minDateForRow(index: number): string {
  if (index === 0) return todayMin.value;
  const previousDate = localQueue.value[index - 1]?.chooseCreatorEstimatedDate;
  if (!previousDate) return todayMin.value;
  const minFromPrevious = DayjsExtended(previousDate).add(DaysBetweenOverlappingVisitChooseCreatorDates, "days").format("YYYY-MM-DD");
  return DayjsExtended.max(DayjsExtended(minFromPrevious), DayjsExtended(todayMin.value)).format("YYYY-MM-DD");
}
</script>

<style scoped lang="scss">
.overlapping-visits-queue-panel {
  &__notice {
    margin-bottom: 16px;
  }

  &__notice-title {
    font-weight: 600;
    margin-bottom: 6px;
  }

  &__notice-subtext {
    margin: 0;
    white-space: pre-line;
  }

  &__list-title {
    margin-bottom: 12px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background: #fafafa;

    &--current {
      border-color: rgba(37, 99, 235, 0.45);
      background: rgba(37, 99, 235, 0.04);
    }
  }

  &__row-main {
    flex: 1;
    min-width: 0;
  }

  &__row-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
  }

  &__visit-name {
    font-weight: 600;
  }

  &__badge {
    font-size: 12px;
    line-height: 1.2;
    padding: 3px 8px;
    border-radius: 999px;

    &--current {
      background: rgba(37, 99, 235, 0.12);
      color: #1d4ed8;
    }

    &--posted {
      background: rgba(22, 163, 74, 0.12);
      color: #15803d;
    }

    &--queued {
      background: rgba(234, 179, 8, 0.2);
      color: #a16207;
    }
  }

  &__send-note {
    margin: 0 0 8px;
    padding-left: 14px;
    font-size: 13px;
    color: #5b5b5b;
  }

  &__date-row {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px 10px;
  }

  &__date-input-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &__late-warning {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  &__estim-note {
    height: 90px;
    min-height: 90px;
  }

  &__date-summary {
    margin: 0;
    font-size: 13px;
  }

  &__date-edit {
    font-size: 13px;
  }

  &__sort-controls {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;
  }

  &__sort-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    background: #fff;
    cursor: pointer;

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }
}
</style>
